/**
 * Dark is the brand default. Light is an explicit choice, and "system" follows
 * the OS. The resolved theme is written to the root element so every stylesheet
 * has one thing to key on, and the browser theme-color follows it.
 */
export type ThemePreference = 'system' | 'dark' | 'light';
export type ResolvedTheme = 'dark' | 'light';

export const themeStorageKey = 'atum-remote.theme.v1';
export const defaultThemePreference: ThemePreference = 'dark';
export const themeColor: Readonly<Record<ResolvedTheme, string>> = {
  dark: '#070b0e',
  light: '#f2f5f6',
};

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'dark' || value === 'light';
}

export function readThemePreference(
  storage: Pick<Storage, 'getItem'> | undefined,
): ThemePreference {
  try {
    const stored = storage?.getItem(themeStorageKey);
    return isThemePreference(stored) ? stored : defaultThemePreference;
  } catch {
    return defaultThemePreference;
  }
}

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersLight: boolean,
): ResolvedTheme {
  if (preference === 'system') return systemPrefersLight ? 'light' : 'dark';
  return preference;
}

export function applyTheme(theme: ResolvedTheme, root: HTMLElement, meta?: Element | null): void {
  if (theme === 'dark') delete root.dataset.theme;
  else root.dataset.theme = theme;
  meta?.setAttribute('content', themeColor[theme]);
}
